import CircularProgress from '@material-ui/core/CircularProgress'
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react'

import { Handler, HandlerOf } from '../../helpers/typeHelper'
import { mergeRefs } from '../../helpers/utils'
import { CoreDocumentAttachment, isCoreDocumentAttachment } from '../../models/document'
import { royalBlue } from '../../theme/colors'
import { FlexBox } from '../Base/FlexBox'
import { Box } from '../Box/Box'
import { Button } from '../Button/Button'
import { Text } from '../Text/Text'
import {
  CancelFile,
  DescriptionIcon,
  DropZoneContainer,
  DropZoneWrap,
  ErrorMessage,
  FileInput,
  FileName,
  FileNameWrapper,
  OnDrop,
  UploadIcon,
} from './DropZoneArea.styles'

export interface DropZoneAreaProps {
  onChange?: HandlerOf<File | undefined> | ((file: File | undefined) => Promise<boolean>)
  onDelete?: Handler
  limit?: number
  hasError?: boolean
  noMargin?: boolean
  initialFile?: File | CoreDocumentAttachment<any> | null
  uploadedFile?: string | null
  label?: React.ReactNode
  className?: string
  isDisabled?: boolean
  error?: React.ReactElement | string
  backgroundColor?: string
}

export const getLimitInMegabytes = (limit: number) => {
  switch (limit) {
    case 10:
      return 8e7
    case 20:
      return 1.6e8
    case 30:
      return 3e7
    default:
      return 3e7
  }
}
export const acceptedFiles = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf']
export const defaultFileSizeLimit = 30

export function fileOnLoad(file: File): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onloadend = (res) => {
      if (res.target?.result) resolve(true)
      else reject(false)
    }
  })
}

export function getUploadedFilename(file: string) {
  return file.split('/').reverse()[0].split('?')[0]
}

export async function cloneFile(file: File) {
  // https://stackoverflow.com/questions/62714319/attached-from-google-drivecloud-storage-in-android-file-gives-err-upload-file
  const buffer = await file.arrayBuffer()
  return new File([buffer], file.name, { type: file.type })
}

function getInitialFilename(initialFile: File | CoreDocumentAttachment<any> | null | undefined) {
  return (
    (initialFile instanceof File && initialFile?.name) ||
    (isCoreDocumentAttachment(initialFile) && initialFile?.originalFilename) ||
    ''
  )
}

export const DropZoneArea = forwardRef(
  (
    {
      onChange,
      onDelete,
      limit = defaultFileSizeLimit,
      noMargin,
      initialFile,
      uploadedFile,
      label,
      error,
      className,
      isDisabled,
      backgroundColor,
      ...restProps
    }: DropZoneAreaProps,
    ref
  ) => {
    const fileUploader = useRef<HTMLInputElement>(null)
    const [dragStatus, setDragStatus] = useState('leave')
    const [fileName, setFileName] = useState(getInitialFilename(initialFile))
    const [hasError, setHasError] = useState<boolean>(!!restProps.hasError || !!error)
    const [isLoadingFile, setIsLoadingFile] = useState(false)

    const validateFile = useCallback(
      async (file?: File) => {
        if (!file) {
          setHasError(true)
          setIsLoadingFile(false)
          return
        }

        if (file.size > getLimitInMegabytes(limit)) {
          setHasError(true)
          setIsLoadingFile(false)
        } else if (!acceptedFiles.includes(file.type)) {
          setHasError(true)
          setIsLoadingFile(false)
        } else {
          setHasError(false)
          try {
            await fileOnLoad(file)
            setFileName(file.name)
            await onChange?.(file)
          } catch (e) {
            setHasError(true)
          }
          setIsLoadingFile(false)
        }
      },
      [onChange, limit]
    )

    const handleFileClickUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation()
      setHasError(false)
      setIsLoadingFile(true)

      const file = e.target.files?.[0]
      if (!file) return
      const clonedFile = await cloneFile(file)
      await validateFile(clonedFile)
    }

    const handleFileDrop = async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()

      setDragStatus('leave')
      setHasError(false)
      setIsLoadingFile(true)
      await validateFile(e.dataTransfer?.files[0])
    }

    const handleDragStatus = useCallback((status: string) => {
      setDragStatus(status)
    }, [])

    const removeFile = useCallback(
      (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        e.stopPropagation()
        setFileName('')
        onChange?.(undefined)
        onDelete?.()
      },
      [onDelete, onChange]
    )

    const handleOnDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
    }

    const handleClick = () => {
      fileUploader.current?.click()
    }

    useEffect(() => {
      setHasError(!!restProps.hasError || !!error)
    }, [restProps.hasError, error])

    return (
      <DropZoneWrap>
        {label && (
          <>
            <Text variant="label4" fontColor="black">
              {label}
            </Text>
            <Box m={0.5} />
          </>
        )}
        <DropZoneContainer
          $backgroundColor={backgroundColor}
          $noMargin={noMargin}
          $hasError={hasError}
          $dragStatus={dragStatus === 'enter'}
          onClick={isDisabled ? undefined : handleClick}
          onDragOver={isDisabled ? undefined : handleOnDragOver}
          onDragEnter={isDisabled ? undefined : () => handleDragStatus('enter')}
          onDragLeave={isDisabled ? undefined : () => handleDragStatus('leave')}
          onDrop={isDisabled ? undefined : handleFileDrop}
          data-testid="dropzone"
          className={className}
        >
          <FileInput ref={mergeRefs([fileUploader, ref])} onChange={handleFileClickUpload} />
          {dragStatus === 'leave' &&
            !fileName &&
            !isLoadingFile &&
            (uploadedFile ? (
              <FileNameWrapper>
                <DescriptionIcon />
                <FileName>{getUploadedFilename(uploadedFile)}</FileName>
                {!isDisabled && (
                  <Button size="medium" variant="secondary">
                    Reupload
                  </Button>
                )}
              </FileNameWrapper>
            ) : (
              <div>
                <FlexBox gap="5px">
                  <UploadIcon />
                  <Text variant="header3" color={royalBlue}>
                    Upload
                  </Text>
                </FlexBox>
              </div>
            ))}
          {dragStatus === 'enter' && (
            <OnDrop>
              <UploadIcon />
              <Text variant="header3" color={royalBlue}>
                Drop the file
              </Text>
            </OnDrop>
          )}
          {fileName && !isLoadingFile && dragStatus === 'leave' && (
            <FileNameWrapper>
              <DescriptionIcon />
              <FileName>{fileName}</FileName>
              <CancelFile onClick={removeFile} />
            </FileNameWrapper>
          )}
          {isLoadingFile && <CircularProgress color="primary" />}
        </DropZoneContainer>
        {(hasError || error) && (
          <ErrorMessage>
            {error ?? 'Upload a supported file format ({limit} MB max – jpg, png, gif, pdf).'}
          </ErrorMessage>
        )}
      </DropZoneWrap>
    )
  }
)
